import { makeAutoObservable } from 'mobx';
import { invalidateNotificationListQuery } from '../../api/hooks/notification/utils';
import { deleteParamsFromUrl } from '../../utils/url';

const SHOW_READ_STORAGE_KEY = 'inbox_show_read';

interface UndoAction {
	type:
		| 'mark_all_read'
		| 'delete_all'
		| 'delete_all_read'
		| 'turn_off_notification'
		| 'delete';
	data: any;
	timestamp: number;
}

interface InboxFeatureStore {
	showRead: boolean;
	showSearch: boolean;
	filterDropdownHeaderMenuVisible: boolean;
	selectedFilters: string[];
	selectedEvents: Array<{ value: string; label: string }>;
	recentlyRead: string[];
	setShowRead: (shouldShow: boolean) => void;
	toggleShowRead: () => void;
	setShowSearch: (show: boolean) => void;
	setFilterDropdownHeaderMenuVisible: (
		filterDropdownHeaderMenuVisible: boolean
	) => void;
	setSelectedFilters: (filters: string[]) => void;
	clearFilters: () => void;
	setSelectedEvents: (events: Array<{ value: string; label: string }>) => void;
	clearSelectedEvents: () => void;
	setEvents: (events: string) => void;
	registerEventsCallback: (callback: (events: string) => void) => void;
	syncEventsWithStore: (
		events: string,
		eventTypes: Array<{ value: string; label: string }>
	) => void;
	addToRecentlyRead: (notificationId: string) => void;
	clearRecentlyRead: () => void;
	isRecentlyRead: (notificationId: string) => boolean;
	removeFromRecentlyRead: (notificationId: string) => void;
	pendingUndoAction: UndoAction | null;
	setPendingUndoAction: (action: UndoAction | null) => void;
	clearUndoAction: () => void;
}

class InboxFeatureStoreImpl implements InboxFeatureStore {
	showRead: boolean;
	showSearch = false;
	filterDropdownHeaderMenuVisible = false;
	selectedFilters: string[] = [];
	selectedEvents: Array<{ value: string; label: string }> = [];
	recentlyRead: string[] = [];
	private eventsCallback: ((events: string) => void) | null = null;
	pendingUndoAction: UndoAction | null = null;

	setShowRead = (shouldShow: boolean) => {
		if (shouldShow) {
			localStorage.setItem(SHOW_READ_STORAGE_KEY, 'true');
		} else {
			localStorage.setItem(SHOW_READ_STORAGE_KEY, 'false');
		}
		this.showRead = shouldShow;
		deleteParamsFromUrl('id');
		invalidateNotificationListQuery();
	};

	toggleShowRead = () => {
		this.clearRecentlyRead();
		this.setShowRead(!this.showRead);
	};

	addToRecentlyRead = (notificationId: string) => {
		if (!this.recentlyRead.includes(notificationId)) {
			this.recentlyRead.push(notificationId);
		}
	};

	clearRecentlyRead = () => {
		this.recentlyRead = [];
	};

	isRecentlyRead = (notificationId: string) =>
		this.recentlyRead.includes(notificationId);

	setShowSearch = (showSearch: boolean) => {
		this.showSearch = showSearch;
	};

	setFilterDropdownHeaderMenuVisible = (
		filterDropdownHeaderMenuVisible: boolean
	) => {
		this.filterDropdownHeaderMenuVisible = filterDropdownHeaderMenuVisible;
	};

	setSelectedFilters = (filters: string[]) => {
		this.selectedFilters = filters;
	};

	clearFilters = () => {
		this.selectedFilters = [];
	};

	setSelectedEvents = (events: Array<{ value: string; label: string }>) => {
		this.selectedEvents = events;
	};

	setEvents = (events: string) => {
		if (this.eventsCallback) {
			this.eventsCallback(events);
		}
	};

	registerEventsCallback = (callback: (events: string) => void) => {
		this.eventsCallback = callback;
	};

	clearSelectedEvents = () => {
		this.selectedEvents = [];
		this.setEvents('');
	};

	syncEventsWithStore = (
		events: string,
		eventTypes: Array<{ value: string; label: string }>
	) => {
		const selectedArr = events ? events.split(',') : [];
		this.setSelectedEvents(
			selectedArr.map((value) => {
				const eventType = eventTypes.find((e) => e.value === value);
				return { value, label: eventType?.label || value };
			})
		);
	};

	removeFromRecentlyRead = (notificationId: string) => {
		this.recentlyRead = this.recentlyRead.filter((id) => id !== notificationId);
	};

	setPendingUndoAction = (action: UndoAction | null) => {
		this.pendingUndoAction = action;
	};

	clearUndoAction = () => {
		this.pendingUndoAction = null;
	};

	constructor() {
		const storedShowRead = localStorage.getItem(SHOW_READ_STORAGE_KEY);
		const initialShowRead =
			storedShowRead === null ? true : storedShowRead === 'true';
		this.showRead = initialShowRead;
		this.recentlyRead = [];
		makeAutoObservable(this);
	}
}

export const inboxFeatureStore = new InboxFeatureStoreImpl();
